import { has } from "lodash";
import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { Card, DateMetricCard } from "..";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { SearchFragmentDateMetrics } from "@radarly/models/search";
import { isDateMetricCardDataviz } from "../dataviz";
import { ComparisonPeriod } from "../period";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";
import { TimeUnit } from "./time-unit";

interface DateMetricCardFactoryOptions {
  from?: AbstractDate;
  metric?: Metric;
  to?: AbstractDate;
  clause?: Clause;
  comparisonPeriod?: ComparisonPeriod;
  timeUnit?: TimeUnit;
}
export class DateMetricCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: DateMetricCardFactoryOptions) {
    const factory = new DateMetricCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input?: DateMetricCardFactoryOptions) {
    const factory = new DateMetricCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    const timeUnit = factory.getTimeUnitFromInput(input);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      metric: factory.getMetricFromCardAndInput(card, input),
      comparisonPeriod: factory.getComparaisonPeriod(
        card,
        "previous_period",
        input
      ),
      timeUnit,
    });
  }
  private getFragments(input?: DateMetricCardFactoryOptions) {
    return this.getComparaisonFragments(
      this.getFragment(input),
      input && has(input, "comparisonPeriod")
        ? input.comparisonPeriod
        : "previous_period"
    );
  }
  private getFragment(
    input?: DateMetricCardFactoryOptions
  ): SearchFragmentDateMetrics<[Metric]> {
    const metric = input?.metric || Metric.Doc;
    const timeUnit = input?.timeUnit || "default";
    return {
      request: {
        query: this.getQuery(input),
        aggs: {
          metrics: { metrics: [metric] },
          date: {
            metrics: [metric],
            interval:
              timeUnit === "default" ? this.getInterval(input) : timeUnit,
          },
        },
      },
    };
  }
}
export class DateMetricCardFactory
  implements ICardFactory<DateMetricCard, DateMetricCardFactoryOptions>
{
  public static getInstance() {
    return new DateMetricCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: DateMetricCardFactoryOptions & {
      dataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<DateMetricCard>().createCard(
      workspaceId,
      projectFocuses,
      "date-metric",
      card.title,
      DateMetricCardFragmentsFactory.createFromCard(card, input),
      this.createDataviz(input?.dataviz || card.dataviz)
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: DateMetricCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<DateMetricCard>().createCard(
      workspaceId,
      projectFocuses,
      "date-metric",
      title,
      DateMetricCardFragmentsFactory.create(input),
      this.createDataviz(
        input?.fullDataviz ??
          getDefaultDataviz(input?.dataviz || DatavizName.HorizontalBarChart)
      )
    );
  }
  public createDataviz(dataviz: Dataviz) {
    if (isDateMetricCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(DatavizName.Kpi) as DateMetricCard["dataviz"];
    }
  }
}
